import type { ITourTranslation } from '@/shared/api/types/tour-types'
import { useTranslation } from 'react-i18next'
import { LocalizedTranslationJobSection } from '@/features/localized-translation'
import { TRANSLATION_JOB_SCOPE } from '@/shared/constants/status'
import { ReasonType } from '@/shared/constants/translation-status'

interface TourTranslationSectionProps {
  /** Danh sách ngôn ngữ được cấu hình và hiển thị thành các tab trong editor. */
  languages: string[]
  /** Danh sách bản dịch name/description của Tour do form cha quản lý. */
  translations: ITourTranslation[]
  /** Hàm trả về bản dịch Tour mới sau khi user sửa hoặc sau khi job nền hoàn tất. */
  onChange: (translations: ITourTranslation[]) => void
  /** Báo cho form cha biết còn nội dung đã sửa nhưng chưa tạo lại bản dịch. */
  onGenerationStateChange?: (hasPendingGeneration: boolean) => void
  /** Id Tour hoặc draft id ổn định dùng để ghép job nền về đúng form. */
  entityId: string
  /** Tên hiển thị tùy chọn trong toast hoặc background jobs panel. */
  entityLabel?: string
  /** Đường dẫn tùy chọn để mở lại Tour từ background jobs panel. */
  entityPath?: string
}

/**
 * Lớp bọc cấu hình workflow dịch nền dùng chung cho Tour.
 */
export function TourTranslationSection({
  languages,
  translations,
  onChange,
  onGenerationStateChange,
  entityId,
  entityLabel,
  entityPath,
}: TourTranslationSectionProps) {
  const { t } = useTranslation()

  return (
    <LocalizedTranslationJobSection<ITourTranslation>
      languages={languages}
      translations={translations}
      onChange={onChange}
      scope={TRANSLATION_JOB_SCOPE.TOUR}
      entityId={entityId}
      {...(entityLabel ? { entityLabel } : {})}
      {...(entityPath ? { entityPath } : {})}
      audioEnabled={ReasonType.TRANSLATE_AUDIO_NOT_AVAILABLE}
      requiredMessage={t('tour.translationRequired')}
      {...(onGenerationStateChange ? { onGenerationStateChange } : {})}
      fields={() => [
        {
          key: 'name',
          label: t('tour.name'),
          placeholder: t('tour.translationNamePlaceholder', {
            defaultValue: 'Nhập tên lộ trình theo ngôn ngữ này',
          }),
        },
        {
          key: 'description',
          label: t('tour.description'),
          placeholder: t('tour.translationDescriptionPlaceholder', {
            defaultValue: 'Nhập mô tả lộ trình theo ngôn ngữ này',
          }),
          multiline: true,
          rows: 5,
        },
      ]}
      translateLabel={t('poi.localizedSpeech.generateSelectedTextOnly')}
      scopeHint={t('poi.localizedSpeech.scopeHint')}
      loadingText={t('poi.localizedSpeech.generatingTextOnly')}
    />
  )
}
